import { useTranslation } from 'react-i18next';
import { FaBuilding, FaCalendarAlt, FaMapMarkerAlt, FaParking } from 'react-icons/fa';
import { LVLO1data } from '../../../../../Data/Projects/LeVIVO LG1/Data_LVLO1';
import ProjectShowcasePage, { ProjectShowcaseMetaItem, ProjectShowcaseSlide } from '../../Shared/ProjectShowcasePage';
import levivoLogo from '../../../../../Assets/images/LeVIVO/Logos/LeVivo-white-circle-logo.png';

function splitNarrative(text: string) {
    const sentences = text.match(/[^.!?]+[.!?]*/g)?.map((sentence) => sentence.trim()).filter(Boolean) ?? [text];

    return {
        lead: sentences.slice(0, 2).join(' '),
        body: sentences.slice(2).length ? [sentences.slice(2).join(' ')] : []
    };
}

export default function LeVivoLongueuil1() {
    const { i18n } = useTranslation();
    const isFrench = i18n.language.startsWith('fr');
    const project = isFrench ? LVLO1data.fr : LVLO1data.en;
    const heroImage = project.images[0]?.url ?? '';
    const storyImage = project.images[2]?.url ?? heroImage;
    const narrative = splitNarrative(project.content.desc[0]);

    const meta: ProjectShowcaseMetaItem[] = [
        {
            label: isFrench ? 'Ville' : 'City',
            value: project.city,
            icon: FaMapMarkerAlt
        },
        {
            label: isFrench ? 'Année' : 'Year',
            value: String(project.yearBuilt),
            icon: FaCalendarAlt
        },
        {
            label: isFrench ? 'Unités' : 'Units',
            value: project.features[0],
            icon: FaBuilding
        },
        {
            label: isFrench ? 'Stationnement' : 'Parking',
            value: project.features[2],
            icon: FaParking
        }
    ];

    const slides: ProjectShowcaseSlide[] = project.images.slice(0, 5).map((slide, index) => ({
        image: slide.url,
        eyebrow: project.city,
        title:
            index === 0
                ? project.name
                : project.features[index - 1] ?? project.name,
        description: project.content.desc[0]
    }));

    return (
        <ProjectShowcasePage
            theme="levivo"
            logo={levivoLogo}
            logoAlt="LeVIVO"
            logoVariant="mark"
            heroImage={heroImage}
            heroAction={{
                label: isFrench ? 'Visiter le site' : 'View website',
                onClick: () => window.open(project.website, '_blank')
            }}
            heroCaption={project.city}
            introEyebrow="LeVIVO"
            title={project.name}
            subtitle={isFrench ? 'Habiter le bord du fleuve à Longueuil' : 'Riverfront rental living in Longueuil'}
            introLead={narrative.lead}
            introBody={narrative.body}
            meta={meta}
            storyEyebrow={project.city}
            storyTitle={
                isFrench
                    ? 'Une adresse résidentielle claire, sobre et durable.'
                    : 'A residential address shaped with clarity, restraint, and durability.'
            }
            storyBody={[
                project.content.desc[0],
                isFrench
                    ? `Le projet se distingue aussi par ${project.features[1].toLowerCase()} et ${project.features[2].toLowerCase()}.`
                    : `The project also stands out through ${project.features[1].toLowerCase()} and ${project.features[2].toLowerCase()}.`
            ]}
            storyImage={storyImage}
            storyImageAlt={project.name}
            storyAction={{
                label: isFrench ? 'Voir le projet' : 'View the project',
                onClick: () => window.open(project.website, '_blank')
            }}
            galleryEyebrow={isFrench ? 'Galerie' : 'Gallery'}
            galleryTitle={isFrench ? 'Quelques vues du projet' : 'A few views of the project'}
            galleryDescription={
                isFrench
                    ? 'Les images prennent davantage de place afin de mieux montrer l’architecture, les façades et l’expérience résidentielle.'
                    : 'The imagery is given more space to better show the architecture, facades, and residential experience.'
            }
            slides={slides}
            previousSlideLabel={isFrench ? 'Image précédente' : 'Previous image'}
            nextSlideLabel={isFrench ? 'Image suivante' : 'Next image'}
            goToSlideLabel={isFrench ? "Aller à l'image" : 'Go to image'}
            featuresEyebrow={isFrench ? 'Faits saillants' : 'Highlights'}
            featuresTitle={isFrench ? 'LeVIVO Longueuil 1 en bref' : 'LeVIVO Longueuil 1 at a glance'}
            featuresLead={
                isFrench
                    ? 'Une réalisation locative compacte, efficace et bien située, portée par une exécution maîtrisée.'
                    : 'A compact, efficient, and well-located rental development delivered through controlled execution.'
            }
            features={project.features}
            asideEyebrow="LeVIVO"
            asideTitle={project.name}
            asideSubtitle={project.address}
            asideBody={
                isFrench
                    ? 'Cette page reprend les informations clés du projet avec une mise en page plus large et une galerie plus utile visuellement.'
                    : 'This page brings the project’s key information together with a wider layout and a more visually useful gallery.'
            }
            asideAction={{
                label: isFrench ? 'Visiter le site LeVIVO' : 'Visit LeVIVO',
                onClick: () => window.open(project.website, '_blank')
            }}
        />
    );
}
